/**
 * StaySearchPage — Traveler-facing accommodation search (Stay22 live).
 *
 * Batch 1 (2026-08-07). Replaces the Hotelbeds sandbox surface for
 * standard travelers. The old `/hotels/*` HB pages remain reachable
 * for `cert_reviewer` accounts (they use `?sandbox=hotelbeds`).
 *
 * Design choices:
 *  • One page — search + results + inline expandable multi-supplier
 *    compare block. Deep-links out to Booking.com / Expedia / etc.
 *    for the actual booking (no in-app booking flow).
 *  • ONE outbound Stay22 request per search — sorting/filtering
 *    happens client-side over the cached response.
 *  • Rate-limit degradation is a soft empty state with auto-retry;
 *    never a hard error page.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Search, MapPin, Users, Calendar, BedDouble, ArrowUpDown,
  Star, ExternalLink, Loader2, RefreshCcw,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import EarnPointsCta from "@/components/EarnPointsCta";

const _MAX_AUTO_RETRIES = 3;

function todayPlus(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function priceCompare(a, b) {
  const av = a.price ?? Number.POSITIVE_INFINITY;
  const bv = b.price ?? Number.POSITIVE_INFINITY;
  return av - bv;
}

function ratingCompare(a, b) {
  const av = a.rating ?? -1;
  const bv = b.rating ?? -1;
  return bv - av;
}

function OfferCard({ offer, onCompareClick, expanded, onExpand, onSupplierClick }) {
  const cheapest = offer.supplier_offers?.[0];
  const supplierCount = offer.supplier_offers?.length || 0;
  // Booking Link Override (2026-08-14) — see TravelerStayCards.jsx.
  // Byte-preserve the CJ (or other manual) URL; do NOT route through
  // /accommodations/click (which appends `campaign=<uuid>`).
  const override = offer.booking_override || null;
  const overrideUrl = override
    ? (offer.deeplink_url || cheapest?.deeplink_url || "")
    : "";
  return (
    <div
      data-testid={`stay-offer-${offer.property_id}`}
      className="rounded-xl border-2 border-tb-navy/15 overflow-hidden bg-white hover:border-tb-navy/40 transition-colors"
    >
      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-0">
        <div className="aspect-[4/3] md:aspect-auto bg-tb-navy/5 relative">
          {offer.image ? (
            <img
              src={offer.image}
              alt={offer.name}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-tb-navy/30">
              <BedDouble className="w-10 h-10" />
            </div>
          )}
        </div>
        <div className="p-4 flex flex-col gap-2">
          <div className="flex items-start justify-between gap-3">
            <h3
              className="text-lg text-tb-navy font-semibold leading-tight"
              style={{ fontFamily: '"Playfair Display", serif' }}
              data-testid={`stay-offer-name-${offer.property_id}`}
            >
              {offer.name}
            </h3>
            {typeof offer.rating === "number" && !Number.isNaN(offer.rating) && (
              <div className="inline-flex items-center gap-1 text-tb-navy/70 text-sm font-hand shrink-0">
                <Star className="w-3.5 h-3.5 fill-tb-navy/70" />
                {offer.rating.toFixed(1)}
              </div>
            )}
          </div>
          {offer.property_type && (
            <div className="text-xs text-tb-navy/50 font-hand uppercase tracking-wide">
              {offer.property_type}
            </div>
          )}
          <div className="mt-auto flex items-end justify-between gap-3">
            <div>
              {offer.price != null ? (
                <>
                  <div className="text-xs text-tb-navy/60 font-hand">
                    from
                  </div>
                  <div
                    className="text-2xl text-tb-navy font-semibold"
                    style={{ fontFamily: '"Playfair Display", serif' }}
                    data-testid={`stay-offer-price-${offer.property_id}`}
                  >
                    {offer.currency || "$"} {Number(offer.price).toFixed(0)}
                  </div>
                  <div className="text-[10px] text-tb-navy/50 font-hand">
                    per night · {supplierCount > 1 ? `${supplierCount} sites` : cheapest?.supplier || "Booking.com"}
                  </div>
                </>
              ) : (
                <div className="text-sm text-tb-navy/60 font-hand">Live rates unavailable</div>
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              {supplierCount > 1 && (
                <button
                  type="button"
                  onClick={() => onExpand()}
                  data-testid={`stay-compare-toggle-${offer.property_id}`}
                  className="h-9 px-3 rounded-full border-2 border-tb-navy/25 text-tb-navy font-hand text-xs hover:bg-tb-navy/5 inline-flex items-center gap-1.5"
                >
                  <ArrowUpDown className="w-3.5 h-3.5" />
                  {expanded ? "Hide sites" : `Compare ${supplierCount} sites`}
                </button>
              )}
              {override && overrideUrl ? (
                <a
                  href={overrideUrl}
                  target="_blank"
                  rel="noopener nofollow noreferrer"
                  data-testid={`stay-book-headline-${offer.property_id}`}
                  data-booking-override={override.provider || "manual_override"}
                  className="h-9 px-3 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-xs inline-flex items-center gap-1.5 no-underline"
                >
                  Book Now
                  <ExternalLink className="w-3 h-3" />
                </a>
              ) : cheapest?.deeplink_url && (
                <a
                  href={cheapest.deeplink_url}
                  target="_blank"
                  rel="noopener sponsored"
                  onClick={(e) => {
                    e.preventDefault();
                    onSupplierClick(offer, cheapest);
                  }}
                  data-testid={`stay-book-headline-${offer.property_id}`}
                  className="h-9 px-3 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-xs inline-flex items-center gap-1.5"
                >
                  Book on {cheapest.supplier}
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
              <EarnPointsCta tone="light" multiplier={offer?.booking_override?.rewards_multiplier ?? 1} />
            </div>
          </div>
        </div>
      </div>

      {expanded && supplierCount > 1 && (
        <div
          data-testid={`stay-supplier-compare-${offer.property_id}`}
          className="border-t border-tb-navy/10 bg-tb-navy/[0.02] p-3 space-y-1.5"
        >
          {offer.supplier_offers.map((s) => (
            <div
              key={s.supplier_code || s.supplier}
              data-testid={`stay-supplier-row-${offer.property_id}-${s.supplier_code}`}
              className="flex items-center justify-between gap-3 text-sm"
            >
              <span className="text-tb-navy font-hand">{s.supplier}</span>
              <span className="text-tb-navy/70 font-mono text-xs">
                {s.price != null
                  ? `${s.currency || "$"} ${Number(s.price).toFixed(0)}`
                  : "—"}
              </span>
              <a
                href={s.deeplink_url}
                target="_blank"
                rel="noopener sponsored"
                onClick={(e) => {
                  e.preventDefault();
                  onSupplierClick(offer, s);
                }}
                data-testid={`stay-book-supplier-${offer.property_id}-${s.supplier_code}`}
                className="h-8 px-3 rounded-full border-2 border-tb-navy/25 text-tb-navy font-hand text-xs hover:bg-tb-navy hover:text-tb-gold-bright inline-flex items-center gap-1"
              >
                Book on {s.supplier}
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


export default function StaySearchPage() {
  const [sp, setSp] = useSearchParams();

  const initialLoc     = sp.get("location") || "";
  const initialCheckIn  = sp.get("check_in")  || todayPlus(14);
  const initialCheckOut = sp.get("check_out") || todayPlus(17);
  const initialAdults   = Number(sp.get("adults") || 2);
  const initialChildren = Number(sp.get("children") || 0);

  const [location, setLocation]     = useState(initialLoc);
  const [checkIn, setCheckIn]       = useState(initialCheckIn);
  const [checkOut, setCheckOut]     = useState(initialCheckOut);
  const [adults, setAdults]         = useState(initialAdults);
  const [children, setChildren]     = useState(initialChildren);
  const [sortBy, setSortBy]         = useState("price");   // "price" | "rating"
  const [maxPrice, setMaxPrice]     = useState("");

  const [offers, setOffers]         = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [rateLimited, setRateLimited] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [expandedId, setExpandedId] = useState(null);
  const [meta, setMeta]             = useState({ cache_hit: false, request_id: "" });

  const doSearch = useCallback(async (attempt = 0) => {
    if (!location.trim()) {
      setError("Enter a city or destination to search.");
      return;
    }
    setLoading(true);
    setError(null);
    setRateLimited(false);
    try {
      const { data } = await api.post("/accommodations/search", {
        location: location.trim(),
        check_in: checkIn,
        check_out: checkOut,
        guests: { adults, children: Array(children).fill(6) },
        page_size: 30,
      });
      if (data?.rate_limited) {
        setRateLimited(true);
        setOffers([]);
        setMeta({ cache_hit: !!data.cache_hit, request_id: data.request_id });
        if (attempt < _MAX_AUTO_RETRIES) {
          const wait = Math.max(500, Number(data.retry_after_ms || 1000));
          setRetryAttempt(attempt + 1);
          setTimeout(() => doSearch(attempt + 1), Math.min(wait, 5000));
        }
        return;
      }
      setOffers(data?.offers || []);
      setMeta({ cache_hit: !!data?.cache_hit, request_id: data?.request_id });
      setRetryAttempt(0);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || "Search failed. Try again.");
    } finally {
      setLoading(false);
    }
  }, [location, checkIn, checkOut, adults, children]);

  // Auto-run on mount if URL had a location.
  useEffect(() => {
    if (initialLoc.trim()) doSearch(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = useCallback((e) => {
    e?.preventDefault?.();
    setSp({
      location, check_in: checkIn, check_out: checkOut,
      adults: String(adults), children: String(children),
    });
    doSearch(0);
  }, [location, checkIn, checkOut, adults, children, doSearch, setSp]);

  const logClick = useCallback((offer, supplier) => {
    // Phase 3 (2026-08-07) — delayed-open pattern:
    //   1. Open a placeholder tab synchronously on the user gesture so
    //      the popup blocker doesn't block us.
    //   2. POST to /accommodations/click — the server appends
    //      `campaign=<campaign_id>` to the deeplink and returns the
    //      new URL, which Stay22's Reporting API will echo back on the
    //      confirmed booking so we can auto-add it to the user's
    //      booked trips.
    //   3. Set the placeholder tab's location to the tracked URL.
    // If step 2 fails, fall back to the raw deeplink so at least the
    // supplier flow still works (attribution lost for that click).
    let tab = null;
    try {
      tab = window.open("about:blank", "_blank");
    } catch { /* popup blocked — fall through */ }
    return api.post("/accommodations/click", {
      property_id:   offer.property_id,
      property_name: offer.name,
      supplier:      supplier.supplier,
      supplier_code: supplier.supplier_code,
      price:         supplier.price,
      currency:      supplier.currency,
      deeplink_used: supplier.deeplink_url,
    }).then(({ data }) => {
      const finalUrl = data?.url || supplier.deeplink_url;
      if (tab && !tab.closed) {
        try { tab.location.href = finalUrl; } catch { /* opener detached */ }
      } else if (finalUrl) {
        window.open(finalUrl, "_blank");
      }
    }).catch(() => {
      if (tab && !tab.closed) {
        try { tab.location.href = supplier.deeplink_url; } catch {}
      }
    });
  }, []);

  // Client-side sort + filter over the cached result set. No new
  // backend request on sort/filter change — the whole point of the
  // 1-request-per-search rule.
  const view = useMemo(() => {
    let arr = offers.slice();
    if (maxPrice) {
      const cap = Number(maxPrice);
      arr = arr.filter(o => o.price == null || o.price <= cap);
    }
    arr.sort(sortBy === "rating" ? ratingCompare : priceCompare);
    return arr;
  }, [offers, sortBy, maxPrice]);

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6" data-testid="stay-search-page">
      <div>
        <h1
          className="text-3xl md:text-4xl text-tb-navy font-semibold"
          style={{ fontFamily: '"Playfair Display", serif' }}
        >
          Find your stay
        </h1>
        <p className="text-sm text-tb-navy/60 font-hand mt-1" data-testid="stay-attribution">
          Search live stays and compare rates from multiple major booking sites in one place.
        </p>
        <p
          className="text-[11px] text-tb-navy/45 font-hand mt-0.5"
          data-testid="stay-commission-disclosure"
        >
          TravelBio may earn a commission when you book through our links.
        </p>
      </div>

      <form
        onSubmit={submit}
        className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_1fr_auto] gap-2 items-end"
        data-testid="stay-search-form"
      >
        <label className="block">
          <span className="text-xs text-tb-navy/60 font-hand inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> Where</span>
          <input
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Paris, France"
            data-testid="stay-search-location"
            className="mt-1 w-full h-10 px-3 rounded-lg border-2 border-tb-navy/20 text-tb-navy font-hand focus:border-tb-navy outline-none"
          />
        </label>
        <label className="block">
          <span className="text-xs text-tb-navy/60 font-hand inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> Check-in</span>
          <input
            type="date"
            value={checkIn}
            onChange={(e) => setCheckIn(e.target.value)}
            data-testid="stay-search-checkin"
            className="mt-1 w-full h-10 px-3 rounded-lg border-2 border-tb-navy/20 text-tb-navy font-hand focus:border-tb-navy outline-none"
          />
        </label>
        <label className="block">
          <span className="text-xs text-tb-navy/60 font-hand inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> Check-out</span>
          <input
            type="date"
            value={checkOut}
            onChange={(e) => setCheckOut(e.target.value)}
            data-testid="stay-search-checkout"
            className="mt-1 w-full h-10 px-3 rounded-lg border-2 border-tb-navy/20 text-tb-navy font-hand focus:border-tb-navy outline-none"
          />
        </label>
        <label className="block">
          <span className="text-xs text-tb-navy/60 font-hand inline-flex items-center gap-1"><Users className="w-3 h-3" /> Guests</span>
          <div className="mt-1 flex gap-1">
            <input
              type="number"
              min={1} max={8}
              value={adults}
              onChange={(e) => setAdults(Math.max(1, Number(e.target.value) || 1))}
              data-testid="stay-search-adults"
              className="w-full h-10 px-2 rounded-lg border-2 border-tb-navy/20 text-tb-navy font-hand focus:border-tb-navy outline-none"
              title="Adults"
            />
            <input
              type="number"
              min={0} max={6}
              value={children}
              onChange={(e) => setChildren(Math.max(0, Number(e.target.value) || 0))}
              data-testid="stay-search-children"
              className="w-full h-10 px-2 rounded-lg border-2 border-tb-navy/20 text-tb-navy font-hand focus:border-tb-navy outline-none"
              title="Children"
            />
          </div>
        </label>
        <button
          type="submit"
          disabled={loading}
          data-testid="stay-search-submit"
          className="h-10 px-5 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm disabled:opacity-50 inline-flex items-center gap-2"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          Search
        </button>
      </form>

      {(offers.length > 0 || loading || rateLimited || error) && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-tb-navy/60 font-hand" data-testid="stay-result-meta">
            {loading && "Checking live availability…"}
            {!loading && offers.length > 0 && (
              <>
                {view.length} of {offers.length} stays
                {meta.cache_hit && <span className="ml-2 opacity-60">· cached</span>}
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-tb-navy/60 font-hand">Sort</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              data-testid="stay-sort"
              className="h-8 px-2 rounded-lg border-2 border-tb-navy/20 text-tb-navy font-hand text-xs focus:border-tb-navy outline-none"
            >
              <option value="price">Price (low → high)</option>
              <option value="rating">Rating (high → low)</option>
            </select>
            <label className="text-xs text-tb-navy/60 font-hand ml-2">Max/night</label>
            <input
              type="number"
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
              placeholder="—"
              data-testid="stay-filter-maxprice"
              className="w-20 h-8 px-2 rounded-lg border-2 border-tb-navy/20 text-tb-navy font-hand text-xs focus:border-tb-navy outline-none"
            />
          </div>
        </div>
      )}

      {rateLimited && (
        <div
          data-testid="stay-rate-limited"
          className="rounded-xl border-2 border-amber-300 bg-amber-50/60 p-4 flex items-center justify-between gap-3"
        >
          <div className="text-sm text-amber-900 font-hand">
            {retryAttempt < _MAX_AUTO_RETRIES
              ? <>Checking availability… one moment (retry {retryAttempt}/{_MAX_AUTO_RETRIES}).</>
              : <>Live availability temporarily unavailable. Try again in a minute.</>}
          </div>
          <button
            type="button"
            onClick={() => doSearch(0)}
            data-testid="stay-manual-retry"
            className="h-9 px-3 rounded-full border-2 border-amber-600 text-amber-900 font-hand text-xs inline-flex items-center gap-1.5"
          >
            <RefreshCcw className="w-3.5 h-3.5" /> Retry
          </button>
        </div>
      )}

      {error && !rateLimited && (
        <div data-testid="stay-error" className="text-sm text-red-700 font-hand p-4 rounded-xl border-2 border-red-200 bg-red-50">
          {error}
        </div>
      )}

      {loading && offers.length === 0 && !rateLimited && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="stay-skeleton">
          {[1,2,3,4].map(i => (
            <div key={i} className="rounded-xl border-2 border-tb-navy/10 h-40 bg-tb-navy/[0.03] animate-pulse" />
          ))}
        </div>
      )}

      {!loading && !rateLimited && offers.length === 0 && !error && location && (
        <div data-testid="stay-empty" className="text-center py-12 text-tb-navy/50 font-hand">
          No stays found. Try a broader location or shift your dates.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {view.map((offer) => (
          <OfferCard
            key={offer.property_id}
            offer={offer}
            expanded={expandedId === offer.property_id}
            onExpand={() => setExpandedId(
              expandedId === offer.property_id ? null : offer.property_id
            )}
            onSupplierClick={logClick}
          />
        ))}
      </div>
    </div>
  );
}
